import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { paymentsApi } from '../../lib/paymentsApi';

export default function ManualPayment({ opportunityId, lineId, summary, onSaved, readOnly = false }) {
    const [amount, setAmount] = useState(((summary?.manualPaid || 0) / 100).toFixed(2));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const save = async () => {
        if (readOnly) return;
        setBusy(true); setError(''); setSaved(false);
        try {
            if (!amount.trim() || !Number.isFinite(Number(amount)) || Number(amount) < 0) throw new Error('Enter a valid amount received.');
            await paymentsApi.manual(opportunityId, lineId, Math.round(Number(amount) * 100));
            await onSaved(); setSaved(true);
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };
    const remainingOutsideStripe = Math.max(0, (summary?.total || 0) - ((summary?.paid || 0) - (summary?.manualPaid || 0)));
    const money = value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((value || 0) / 100);
    return <Stack spacing={2.5}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: { xs: 1, sm: 2 } }}>
            {[
                ['Line total', summary?.total, false],
                ['Received', summary?.paid, false],
                ['Remaining', summary?.balance, true],
            ].map(([label, value, accent]) => <Box key={label} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: accent ? '#edf4ff' : '#f5f7fa', border: '1px solid', borderColor: accent ? '#d6e5fb' : '#edf0f4' }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.75 }}>{label}</Typography>
                <Typography sx={{ fontSize: { xs: 17, sm: 23 }, fontWeight: 700, letterSpacing: '-0.5px', color: accent ? '#185cba' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{summary ? money(value) : '—'}</Typography>
            </Box>)}
        </Box>
        <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>Record a payment</Typography>
            <Typography variant="body2" color="text.secondary">Track cash, check, or bank transfers received outside Stripe.</Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {saved && <Alert severity="success">Payment updated.</Alert>}
        {summary?.hasActive && <Alert severity="info">Resolve or void the outstanding Stripe request before recording an outside payment.</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
            <TextField disabled={readOnly} fullWidth size="small" label="Total received outside Stripe ($)" type="number" value={amount} onChange={e => { setAmount(e.target.value); setSaved(false); }} inputProps={{ min: 0, step: '0.01' }} helperText={readOnly ? 'Only the owner can record a payment.' : 'Total to date. Enter 0 to clear a manual entry.'} />
            <Button variant="outlined" sx={{ flexShrink: 0, minHeight: 40, textTransform: 'none', borderRadius: 2 }} disabled={readOnly || !summary || busy || summary.hasActive} onClick={() => setAmount((remainingOutsideStripe / 100).toFixed(2))}>Set full amount</Button>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" disableElevation sx={{ textTransform: 'none', borderRadius: 2, px: 3, minHeight: 40 }} disabled={readOnly || !summary || busy || summary.hasActive} onClick={save}>{busy ? 'Saving…' : 'Save payment'}</Button>
        </Stack>
    </Stack>;
}
